import React, { useState, useRef, useCallback } from "react";
import {
  FiUpload,
  FiDownload,
  FiX,
  FiFile,
  FiCheck,
  FiAlertCircle,
  FiTrash2,
  FiLock,
  FiEye,
  FiEyeOff
} from "react-icons/fi";
import { bulkImportCredentials, BulkImportEntryData, CredentialType } from "../../utils/api/endpoints/vault";
import { encryptCredential, encryptNotes } from "../../utils/crypto/vault-crypto";

interface ParsedCredential {
  label: string;
  credential_type: CredentialType;
  url: string;
  username: string;
  password: string;
  notes: string;
  is_favorite: boolean;
  expires_at: string;
  rotation_reminder_days: number | null;
  isValid: boolean;
  error?: string;
}

interface BulkImportModalProps {
  vaultId: number;
  vaultName: string;
  onClose: () => void;
  onImportComplete: () => void;
}

const VALID_TYPES: CredentialType[] = [
  "password", "api_key", "ssh_key", "certificate", "database", "server",
  "secure_note", "wifi", "credit_card", "custom"
];

const BulkImportModal: React.FC<BulkImportModalProps> = ({
  vaultId,
  vaultName,
  onClose,
  onImportComplete
}) => {
  const [step, setStep] = useState<"upload" | "preview" | "importing" | "complete">("upload");
  const [parsedData, setParsedData] = useState<ParsedCredential[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [masterPassword, setMasterPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors?: string[] } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [parseError, setParseError] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse CSV content
  const parseCSV = useCallback((content: string): ParsedCredential[] => {
    const lines = content.trim().split("\n");
    if (lines.length < 2) throw new Error("CSV must have header row and at least one data row");

    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const requiredHeaders = ["label", "password"];
    for (const req of requiredHeaders) {
      if (!headers.includes(req)) throw new Error(`Missing required column: ${req}`);
    }

    const results: ParsedCredential[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === 0 || values.every(v => v.trim() === "")) continue;

      const row: Record<string, string> = {};
      headers.forEach((h, idx) => { row[h] = values[idx]?.trim() || ""; });

      const credType = (row.credential_type || "password") as CredentialType;
      const isValidType = VALID_TYPES.includes(credType);

      // Parse is_favorite (accepts true, false, yes, no, 1, 0)
      const isFavorite = ["true", "yes", "1"].includes((row.is_favorite || "").toLowerCase());

      // Parse rotation_reminder_days
      const rotationDays = row.rotation_reminder_days ? parseInt(row.rotation_reminder_days, 10) : null;

      results.push({
        label: row.label || "",
        credential_type: isValidType ? credType : "password",
        url: row.url || "",
        username: row.username || "",
        password: row.password || "",
        notes: row.notes || "",
        is_favorite: isFavorite,
        expires_at: row.expires_at || "",
        rotation_reminder_days: isNaN(rotationDays as number) ? null : rotationDays,
        isValid: !!row.label && !!row.password && isValidType,
        error: !row.label ? "Label required" : !row.password ? "Password required" : !isValidType ? `Invalid type: ${row.credential_type}` : undefined
      });
    }
    return results;
  }, []);

  // Parse CSV line handling quoted values
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Parse JSON content
  const parseJSON = useCallback((content: string): ParsedCredential[] => {
    const data = JSON.parse(content);
    const credentials = data.credentials || data;
    if (!Array.isArray(credentials)) throw new Error("JSON must contain an array of credentials");

    return credentials.map((cred: any) => {
      const credType = (cred.credential_type || "password") as CredentialType;
      const isValidType = VALID_TYPES.includes(credType);
      return {
        label: cred.label || "",
        credential_type: isValidType ? credType : "password",
        url: cred.url || "",
        username: cred.username || "",
        password: cred.password || "",
        notes: cred.notes || "",
        is_favorite: cred.is_favorite === true || cred.is_favorite === "true",
        expires_at: cred.expires_at || "",
        rotation_reminder_days: typeof cred.rotation_reminder_days === "number" ? cred.rotation_reminder_days : null,
        isValid: !!cred.label && !!cred.password && isValidType,
        error: !cred.label ? "Label required" : !cred.password ? "Password required" : !isValidType ? `Invalid type` : undefined
      };
    });
  }, []);

  // Handle file selection
  const handleFile = useCallback((file: File) => {
    setParseError("");
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        let parsed: ParsedCredential[];
        if (file.name.endsWith(".json")) {
          parsed = parseJSON(content);
        } else if (file.name.endsWith(".csv")) {
          parsed = parseCSV(content);
        } else {
          throw new Error("Unsupported file format. Please use CSV or JSON.");
        }
        if (parsed.length === 0) throw new Error("No credentials found in file");
        if (parsed.length > 100) throw new Error("Maximum 100 credentials per import");
        setParsedData(parsed);
        setStep("preview");
      } catch (err: any) {
        setParseError(err.message || "Failed to parse file");
        setParsedData([]);
      }
    };
    reader.readAsText(file);
  }, [parseCSV, parseJSON]);

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  // Remove invalid entries
  const removeEntry = (index: number) => {
    setParsedData(prev => prev.filter((_, i) => i !== index));
  };

  // Download template
  const downloadTemplate = (format: "csv" | "json") => {
    const link = document.createElement("a");
    link.href = `/sample-files/credential-import-template.${format}`;
    link.download = `credential-import-template.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle import
  const handleImport = async () => {
    if (!masterPassword) return;

    const validEntries = parsedData.filter(p => p.isValid);
    if (validEntries.length === 0) return;

    setStep("importing");
    setImportProgress(0);

    try {
      const encryptedEntries: BulkImportEntryData[] = [];

      for (let i = 0; i < validEntries.length; i++) {
        const cred = validEntries[i];
        setImportProgress(Math.round((i / validEntries.length) * 50));

        // Encrypt the password
        const encrypted = await encryptCredential(
          { entries: [{ key: "password", value: cred.password, type: "password" }] },
          masterPassword
        );

        // Encrypt notes if present
        let notesData = null;
        if (cred.notes) {
          const saltBytes = Uint8Array.from(atob(encrypted.salt), c => c.charCodeAt(0));
          notesData = await encryptNotes(cred.notes, masterPassword, saltBytes);
        }

        encryptedEntries.push({
          label: cred.label,
          credential_type: cred.credential_type,
          url: cred.url || undefined,
          username: cred.username || undefined,
          encrypted_data: encrypted.encrypted_data,
          iv: encrypted.iv,
          auth_tag: encrypted.auth_tag,
          salt: encrypted.salt,
          key_iterations: encrypted.key_iterations,
          encrypted_notes: notesData?.encrypted_notes,
          notes_iv: notesData?.notes_iv,
          notes_auth_tag: notesData?.notes_auth_tag,
          is_favorite: cred.is_favorite,
          expires_at: cred.expires_at || undefined,
          rotation_reminder_days: cred.rotation_reminder_days || undefined
        });
      }

      setImportProgress(75);

      const result = await bulkImportCredentials(vaultId, encryptedEntries);

      setImportProgress(100);
      setImportResult({
        imported: result.imported,
        failed: result.failed,
        errors: result.errors
      });
      setStep("complete");
    } catch (err: any) {
      setImportResult({
        imported: 0,
        failed: validEntries.length,
        errors: [err.message || "Import failed"]
      });
      setStep("complete");
    }
  };

  const validCount = parsedData.filter(p => p.isValid).length;
  const invalidCount = parsedData.length - validCount;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Dark Header */}
        <div className="px-6 py-4 bg-slate-800 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-700 rounded-lg">
              <FiUpload className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Import Credentials</h2>
              <p className="text-slate-400 text-sm">Vault: {vaultName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-slate-700 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === "upload" && (
            <div className="space-y-6">
              {/* Download Templates */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-gray-900 font-medium mb-3">1. Download Template</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Download a sample template, fill in your credentials, then upload the file.
                </p>
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={() => downloadTemplate("csv")}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FiDownload /> CSV Template
                  </button>
                  <button
                    onClick={() => downloadTemplate("json")}
                    className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    <FiDownload /> JSON Template
                  </button>
                </div>

                {/* Field Reference */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                  <h4 className="text-green-700 font-medium mb-2">📋 Field Reference</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="text-gray-700">
                      <span className="text-red-500">*</span> label
                      <span className="text-gray-500 ml-1">- Name for the credential</span>
                    </div>
                    <div className="text-gray-700">
                      <span className="text-red-500">*</span> password
                      <span className="text-gray-500 ml-1">- Secret value to encrypt</span>
                    </div>
                    <div className="text-gray-600">
                      credential_type
                      <span className="text-gray-400 ml-1">- password, api_key, ssh_key, database, server, certificate, wifi, secure_note, credit_card, custom</span>
                    </div>
                    <div className="text-gray-600">
                      url
                      <span className="text-gray-400 ml-1">- Service URL</span>
                    </div>
                    <div className="text-gray-600">
                      username
                      <span className="text-gray-400 ml-1">- Username/identifier</span>
                    </div>
                    <div className="text-gray-600">
                      notes
                      <span className="text-gray-400 ml-1">- Additional notes</span>
                    </div>
                    <div className="text-gray-600">
                      is_favorite
                      <span className="text-gray-400 ml-1">- true/false</span>
                    </div>
                    <div className="text-gray-600">
                      expires_at
                      <span className="text-gray-400 ml-1">- YYYY-MM-DD format</span>
                    </div>
                    <div className="text-gray-600">
                      rotation_reminder_days
                      <span className="text-gray-400 ml-1">- Days before expiry alert</span>
                    </div>
                  </div>
                  <p className="text-gray-500 mt-2"><span className="text-red-500">*</span> = Required field</p>
                </div>
              </div>

              {/* Upload Area */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-gray-900 font-medium mb-3">2. Upload Your File</h3>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? "border-green-400 bg-green-50" : "border-gray-300 hover:border-gray-400"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <FiFile className="mx-auto text-4xl text-gray-400 mb-3" />
                  <p className="text-gray-600 mb-2">
                    Drag and drop your CSV or JSON file here, or
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Browse Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.json"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  />
                </div>
                {parseError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                    <FiAlertCircle /> {parseError}
                  </div>
                )}
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-900">
                  <FiFile className="text-green-600" />
                  <span>{fileName}</span>
                  <span className="text-gray-500">({parsedData.length} entries)</span>
                </div>
                <button
                  onClick={() => { setStep("upload"); setParsedData([]); setFileName(""); }}
                  className="text-gray-400 hover:text-red-500"
                >
                  <FiTrash2 />
                </button>
              </div>

              {/* Summary */}
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-green-600">
                  <FiCheck /> {validCount} valid
                </div>
                {invalidCount > 0 && (
                  <div className="flex items-center gap-2 text-red-500">
                    <FiAlertCircle /> {invalidCount} invalid
                  </div>
                )}
              </div>

              {/* Preview Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-700 sticky top-0">
                      <tr className="text-white text-left">
                        <th className="p-3 font-medium">Label</th>
                        <th className="p-3 font-medium">Type</th>
                        <th className="p-3 font-medium">Username</th>
                        <th className="p-3 font-medium">URL</th>
                        <th className="p-3 font-medium">Status</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((cred, idx) => (
                        <tr key={idx} className={`border-t border-gray-200 ${!cred.isValid ? "bg-red-50" : ""}`}>
                          <td className="p-3 text-gray-900">{cred.label || "-"}</td>
                          <td className="p-3 text-gray-600">{cred.credential_type}</td>
                          <td className="p-3 text-gray-600">{cred.username || "-"}</td>
                          <td className="p-3 text-gray-600 truncate max-w-[150px]">{cred.url || "-"}</td>
                          <td className="p-3">
                            {cred.isValid ? (
                              <span className="text-green-600 flex items-center gap-1"><FiCheck /> Valid</span>
                            ) : (
                              <span className="text-red-500 flex items-center gap-1"><FiAlertCircle /> {cred.error}</span>
                            )}
                          </td>
                          <td className="p-3">
                            <button onClick={() => removeEntry(idx)} className="text-gray-400 hover:text-red-500">
                              <FiTrash2 />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Master Password */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-gray-900 font-medium mb-3 flex items-center gap-2">
                  <FiLock className="text-green-600" /> Enter Master Password
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Your master password is required to encrypt the credentials before storing.
                </p>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    placeholder="Enter your vault master password"
                    className="w-full bg-white text-gray-900 rounded-lg px-4 py-3 pr-12 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === "importing" && (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full border-4 border-green-500 border-t-transparent animate-spin"></div>
              <h3 className="text-xl text-gray-900 mb-2">Importing Credentials...</h3>
              <p className="text-gray-500 mb-4">Encrypting and storing your credentials securely</p>
              <div className="w-full max-w-xs mx-auto bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${importProgress}%` }}></div>
              </div>
              <p className="text-gray-500 text-sm mt-2">{importProgress}%</p>
            </div>
          )}

          {step === "complete" && importResult && (
            <div className="text-center py-8">
              {importResult.imported > 0 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                    <FiCheck className="text-3xl text-green-600" />
                  </div>
                  <h3 className="text-xl text-gray-900 mb-2">Import Complete!</h3>
                  <p className="text-gray-600">
                    Successfully imported <span className="text-green-600 font-bold">{importResult.imported}</span> credentials
                    {importResult.failed > 0 && (
                      <>, <span className="text-red-500">{importResult.failed} failed</span></>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <FiAlertCircle className="text-3xl text-red-500" />
                  </div>
                  <h3 className="text-xl text-gray-900 mb-2">Import Failed</h3>
                  <p className="text-gray-600">{importResult.errors?.[0] || "An error occurred during import"}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end gap-3 bg-teal-50 rounded-b-2xl">
          {step === "preview" && (
            <>
              <button onClick={() => { setStep("upload"); setParsedData([]); }} className="px-4 py-2.5 text-gray-600 hover:text-gray-900 font-medium rounded-lg hover:bg-white transition-colors">
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={validCount === 0 || !masterPassword}
                className="px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-colors"
              >
                <FiUpload /> Import {validCount} Credentials
              </button>
            </>
          )}
          {step === "complete" && (
            <button
              onClick={() => { onImportComplete(); onClose(); }}
              className="px-6 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors"
            >
              Done
            </button>
          )}
          {step === "upload" && (
            <button onClick={onClose} className="px-4 py-2.5 text-gray-600 hover:text-gray-900 font-medium rounded-lg hover:bg-white transition-colors">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;

